import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, MessageCircle, Users, Clock, CheckCircle, 
  AlertTriangle, Eye, UserPlus, RefreshCw, ArrowRight, X
} from 'lucide-react';
import { useChat, Conversation } from '../../hooks/useChat';
import ChatWindow from '../../components/chat/ChatWindow';
import { useSupabase } from '../../contexts/SupabaseContext';

const MessageManagement = () => {
  const { user } = useSupabase();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  
  const {
    conversations,
    messages,
    loading,
    fetchConversations,
    fetchMessages,
    sendMessage,
    joinConversation
  } = useChat();

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation, fetchMessages]);

  const filteredConversations = conversations.filter(conversation => {
    // Filter by search query
    const participantNames = conversation.participants
      .filter(p => p.user)
      .map(p => `${p.user?.first_name} ${p.user?.last_name}`.toLowerCase());
    
    const matchesSearch = participantNames.some(name => name.includes(searchQuery.toLowerCase())) ||
      (conversation.topic && conversation.topic.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filter by status
    const matchesStatus = filterStatus === 'all' || conversation.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleSelectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    
    // Check if agent is already a participant
    const isParticipant = conversation.participants.some(
      p => p.user_id === user?.id && p.role === 'agent'
    );
    
    // If not, join the conversation as an agent
    if (!isParticipant && user) {
      await joinConversation(conversation.id, 'agent');
      // Refresh conversation data
      await fetchConversations();
    }
    
    await fetchMessages(conversation.id);
  };

  const handleCloseConversation = () => {
    setSelectedConversation(null);
  };

  const handleSendMessage = async (content: string, attachmentUrl?: string, attachmentType?: string) => {
    if (!selectedConversation) return;
    await sendMessage(selectedConversation.id, content, attachmentUrl, attachmentType);
  };

  const getParticipantNames = (conversation: Conversation): string => {
    return conversation.participants
      .filter(p => p.user)
      .map(p => `${p.user?.first_name} ${p.user?.last_name}`)
      .join(', ');
  };

  const getParticipantRoles = (conversation: Conversation): string => {
    return conversation.participants
      .map(p => p.role)
      .filter((value, index, self) => self.indexOf(value) === index) // unique values
      .join(', ');
  };

  const getLastMessagePreview = (conversation: Conversation): string => {
    if (!conversation.last_message) return 'No messages yet';
    
    const content = conversation.last_message.content;
    return content.length > 40 ? `${content.substring(0, 40)}...` : content;
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    
    // If today, show time
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // If this year, show month and day
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    
    // Otherwise show date
    return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </span>;
      case 'archived':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Archived
        </span>;
      case 'closed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Closed
        </span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Message Management</h1>
              <p className="text-gray-600">Monitor and participate in customer conversations</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => fetchConversations()}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations by participant or topic..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Conversations</p>
                <p className="text-2xl font-bold text-gray-900">{conversations.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">
                  {conversations.filter(c => c.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-gray-900">
                  {conversations.filter(c => (c.unread_count || 0) > 0).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg. Response Time</p>
                <p className="text-2xl font-bold text-gray-900">2.5h</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Conversations List */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
            </div>
            
            <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 p-4 text-center">
                  <MessageCircle className="h-8 w-8 text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">No conversations found</p>
                </div>
              ) : (
                <div>
                  {filteredConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation)}
                      className={`w-full text-left px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        selectedConversation?.id === conversation.id ? 'bg-orange-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium text-gray-900">
                            {conversation.topic || getParticipantNames(conversation)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {getParticipantRoles(conversation)}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(conversation.status)}
                          <div className="text-xs text-gray-500">
                            {conversation.last_message 
                              ? formatTimestamp(conversation.last_message.created_at)
                              : formatTimestamp(conversation.updated_at)}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {getLastMessagePreview(conversation)}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-xs text-gray-500">
                          {conversation.participants.length} participants
                        </div>
                        {(conversation.unread_count && conversation.unread_count > 0) ? (
                          <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                            {conversation.unread_count} unread
                          </span>
                        ) : (
                          <span className="text-xs text-green-600 flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Read
                          </span>
                        )}
                      </div>
                      {/* Check if agent is already a participant */}
                      {!conversation.participants.some(p => p.user_id === user?.id && p.role === 'agent') && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectConversation(conversation);
                            }}
                            className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded flex items-center space-x-1"
                          >
                            <UserPlus className="h-3 w-3" />
                            <span>Join as Agent</span>
                            <ArrowRight className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Chat Window */}
          <div className="lg:col-span-2 h-[600px]">
            <ChatWindow
              conversation={selectedConversation}
              messages={messages}
              onSendMessage={handleSendMessage}
              onClose={handleCloseConversation}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageManagement;